import { makeNavigationLink } from './ActivityLink'

import { lionsTigersAndBearsLink } from './LionsTigerAndBears'
import { externalLink } from '../DataAndAPIs/Links'


const renderArchitectureDesignLewis = () => {
	return ( <div className="architecture-design-lewis">
		<h3>{architectureDesignLewis.name}</h3>
		<h5>Summary</h5>
		<p>In this activity you will be designing the software architecture that the Lewis University Computer Science 
		department will use when teaching classes. This is the architecture that students will use when they take 
		classes ranging from Introduction to Computer Science to Graduate-level Capstone classes. The platform does not 
		need to scale to thousands of concurrent users, but it does need to support everything from a simple “Hello 
		World” website to relatively complex, multi-tenant software-as-a-service applications. Initially, it does 
		not need to support native applications (i.e. Windows, iOS, Android, etc.), but it will eventually need to 
		expand to these areas. Finally, the architecture cannot require university resources for setting up or 
		maintaining infrastructure. The student and/or team should be capable of managing everything.</p>

		<p>Remember that instructors are focused on teaching concepts and the time necessary time to learn new tools
		should be minimized.</p>

		<h5>Objectives</h5>
		Students will: 
		<ul>
			<li>Identify Architecturally Significant Requirements (ASRs) from a problem statement.</li>
			<li>Justify why these requirements are architecturally significant.</li>
			<li>Design a high-level architecture that addresses the ASRs.</li>
			<li>Reflect on trade-offs and architectural decisions</li>
		</ul>

		<h5>Scenario</h5>
		You are tasked with designing the architecture for the Lewis University Computer Science department to 
		use when teaching important concepts in classes. The system will allow: 
		<ul>
			<li>Classes ranging from Introduction to Computer Science to Graduate-level Capstone classes to leverage the architecture.</li>
			<li>Professors and students to be able quickly implement and learn the architecture so that they can focus on learning concepts.</li>
			<li>Students to be able to manage all assets without need for professor or university resources.</li>
			<li>Zero of very low cost.</li>
		</ul>
		The system must support:
		<ul>
			<li>Web-based clients written in HTML, CSS, and JavaScript or React that are accessible from any device.</li>
			<li>Integration with external authentication providers (e.g., Google, Microsoft, GitHub).</li>
			<li>Initially JavaScript (NodeJS) severs eventually expanding to Python, and Java servers</li>
			<li>Integration with external authentication providers (e.g., Google, Microsoft, GitHub).</li>
			<li>NoSQL database eventually expanding to SQL</li>
			<li>File and large file storage and hosting (not in a database).</li>
			<li>Logging and monitoring for system health and misuse detection.</li>
			<li>A reference implementation on MS Azure or Google Firebase.</li>
			<li>Detailed up to date documentation, examples, and tutorials demonstrating key usage patterns.</li>
		</ul>

		<h5>Requirements</h5>
		<em>Requirement 1: Identify ASRs</em>
		<ol type='n'>
			<li>From the scenario, list at least 5 architecturally significant requirements.</li>
			<li>For each scenario</li>
			<ul>
				<li>Categorize it (e.g., performance, availability, modifiability, security).</li>
				<li>Explain why it is architecturally significant.</li>
			</ul>
		</ol>

		<em>Requirement 2: Define Quality Attribute Scenarios</em>
		<ol type='n'>
			<li>Choose 3 ASRs and create a quality attribute scenario for each using this structure: [Stimulus] causes the 
			system to respond [response] while in [context].</li>
			<li>Example: An undergraduate Software Engineering team needs to create a Service-Oriented-Architecture (SOA) 
			application for their Class Project. The application will need a React client, a Node.js or equivalent server,
			authentication via Google or GitHub credentials, a NoSQL database, and a place to store large files (200MB 
			plus videos). Client React website</li>
		</ol>

		<em>Requirement 3: Design a High-Level Architecture</em>
		<ol type='n'>
			<li>Sketch a high-level architectural diagram:</li>
			<ol type='a'>
				<li>Include components/modules, key technologies (you can propose them), and interfaces.</li>
				<li>Indicate where the ASRs are being addressed (e.g., caching layer, auth service, load balancer)..</li>
			</ol>
			<li>Justify your architecture:</li>
			<ol type='a'>
				<li>How does it satisfy the ASRs?</li>
				<li>What design patterns or tactics did you apply (e.g., layered, microservices, broker, etc.)?</li>
				<li>What trade-offs did you consider (e.g., performance vs. security)?</li>
			</ol>
		</ol>

		<em>Requirement 4: Submit a written report in PDF format including:</em>
		<ol type='n'>
			<li>ASR list + justifications</li>
			<li>Quality attribute scenarios</li>
			<li>Architectural diagram (hand-drawn or digital)</li>
			<li>Design rationale</li>
		</ol>
	</div>)
}

export const architectureDesignLewis = { 'name':'Architecture Design: Lewis Instructional Software Architecture', 'route':'architecture-design-lewis', 'render':renderArchitectureDesignLewis }
export const architectureDesignLewisLink = () => { return makeNavigationLink(architectureDesignLewis) }

const renderArchitectureDesignLewisPhase1 = () => {
	return ( <div className="architecture-design-lewis">
		<h3>{architectureDesignLewisPhase1.name}</h3>
		<h5>Summary</h5>
		<p>In this activity you will work as part of a team (2-4 classmates) to take what each of you did 
		in {architectureDesignLewisLink()}, consolidate everyone's work into a single <em>simplified architecture 
		design</em>, document the architecture, and begin implementing the architecture by creating an initial 
		reference implementation.</p>

		<h5>Objectives</h5>
		Students will: 
		<ul>
			<li>Work as part of a team to consolidate, document, and simplify their Lewis Instructional Software Architecture.</li>
			<li><em>Succinctly</em> document the resulting architecture</li>
			<li>Provide a initial client only CI/CD reference implementation with GitHub and Azure, Firebase, AWS, or Docker.</li>
			<li>Plan for the expansion of the reference implementation to include Authentication and NoSQL data storage.</li>
		</ul>

		<h5>Scenario</h5>
		<p>What a wonderful job you did completing {architectureDesignLewisLink()}. Now you have been assigned as part 
		of a team tasked with consolidating, simplifying, and providing a reference implementation for the Lewis 
		Instructional Architecture on Azure, Firebase, or AWS. We will start small with the reference implementation
		being limited to a React client application deployed with CI/CD using GitHub and Azure Static Web Apps, Google 
		Firebase Hosting, AWS, or Docker.</p>

		<p>The initial reference implementation must:</p>
		<ul>
			<li>Demonstrate a React web application deployed as a static website running on the selected cloud provider.</li>
			<li>Store all source code stored in shared GitHub repository.</li>
			<li>Provide full CI/CD integration such that team members can test locally, update code in GitHub, and code is automatically deployed</li>
			<li>Include a tutorial and updated documentation demonstrating the above deliverables that can be completed 15 minutes</li>
		</ul>

		<h5>Requirements</h5>
		<em>Requirement 1: Consolidate and  <em>simplify</em> architecture documentation by:</em>
		<ol type='n'>
			<li>Updating architectural diagrams (hand-drawn or digital) so they are <em>beautiful</em></li>
			<li>Updating categorized Architecturally Significant Requirements</li>
			<li>Updating Quality Attribute Scenarios and justifications</li>
		</ol>

		<em>Requirement 2: Create a reference implementation by:</em>
		<ol type='n'>
			<li>Implementing {lionsTigersAndBearsLink()} in React utilizing your architecture and cloud provider</li>
			<li>Implementing a CI/CD pipeline with GitHub and Azure Static Web Apps, Google Firebase Hosting, AWS, or Docker</li>
			<li>Ensuring that team members can develop locally, push code to GitHub, and the have code automatically deployed</li>
		</ol>

		<em>Requirement 3: Create a tutorial that can recreate Requirement 2 in less than 15 minutes by:</em>
		<ol type='n'>
			<li>Publishing a written and/or video tutorial that can implement Requirement 2 in less than 15 minutes</li>
			<li>Utilizing  a GitHub template repository to provide the initial code and to publish the written tutorial</li>
			<li>Note: If you choose to create a video tutorial, you will need to host it someplace else</li>
		</ol>

		<em>Requirement 4: Add a Domain Name to your reference implementation by:</em>
		<ol type='n'>
			<li>Purchasing a low cost Domain Name from GoDaddy</li>
			<li>Configuring the Domain Name to point to your cloud provider hosted reference implementation</li>
			<li>Create a second tutorial that demonstrates how to add a domain name to a website in your cloud provider</li>
		</ol>
	</div>)
}

export const architectureDesignLewisPhase1 = { 'name':'Lewis Instructional Software Architecture - Phase 1', 'route':'architecture-lewis-phase-1', 'render':renderArchitectureDesignLewisPhase1 }
export const architectureDesignLewisPhase1Link = () => { return makeNavigationLink(architectureDesignLewisPhase1) }

const renderArchitectureDesignLewisPhase2 = () => {
	const firebaseTutorial = () => externalLink('Firebase Authentication Tutorial (preliminary)', 'https://lewisu.hosted.panopto.com/Panopto/Pages/Viewer.aspx?pid=254fa8f1-e901-4516-8791-b393002f44fb') 

	return ( <div className="architecture-design-lewis">
		<h3>{architectureDesignLewisPhase2.name}</h3>
		<h5>Summary</h5>
		<p>In this activity you will continue to work as part of a team to update and enhance what you delivered 
		in {architectureDesignLewisPhase1Link()}. In this sprint you will add that several additional key application 
		architecture elements to your reference implementation. First, you finish adding a Domain Name to your 
		architecture. Second, you will add 3rd party authentication.</p>

		<p>Be sure to host an instance of your reference implementation on your selected cloud provider.</p>

		<h5>Objectives</h5>
		Students will: 
		<ul>
			<li><em>Host a fully functional instance of their reference implementation on a selected cloud provider</em></li>
			<li>Work as part of a team to consolidate, document, and simplify their Lewis Instructional Software Architecture.</li>
			<li><em>Succinctly</em> document the resulting architecture</li>
			<li>Provide a initial client only CI/CD reference implementation with GitHub and Azure, Firebase, AWS, or Docker.</li>
			<li>Plan for the expansion of the reference implementation to include Authentication and NoSQL data storage.</li>
		</ul>

		<h5>Scenario</h5>
		<p>You and your team did a wonderful job implementing {architectureDesignLewisLink()} and {architectureDesignLewisPhase1Link()}. 
		Now your team has been assigned as to enhance the architecture by adding secure NoSQL data storage and file upload 
		capabilities to the Lewis Instructional Architecture on Azure, Firebase, or AWS. </p>

		<p>The reference implementation must continue to:</p>
		<ul>
			<li>Demonstrate a React web application deployed as a static website running on the selected cloud provider.</li>
			<li>Store all source code stored in shared GitHub repository.</li>
			<li>Provide full CI/CD integration such that team members can test locally, update code in GitHub, and code is automatically deployed</li>
			<li>Include a tutorial and updated documentation demonstrating the above deliverables that can be completed 15 minutes</li>
		</ul>

		<p>Now it must be enhanced to include secure NoSQL data storage and file upload capabilities to:</p>
		<ul>
			<li>Demonstrate your React web application calling server-side SOA service.</li>
			<li>Demonstrate uploading a image (png) and small video (mp4) file to your cloud provider.</li>
			<li>Provide full CI/CD integration such that team members can test locally, update code in GitHub, and code is automatically deployed</li>
			<li>Include a tutorial and updated documentation demonstrating the above deliverables that can be completed 15 minutes</li>
		</ul>

		<p>Note that requirements 1 through 3 from {architectureDesignLewisPhase1Link()} are not repeated. Requirement 
		4 is included again this sprint as it was not required to complete requirement 4 last sprint. 
		</p>

		<h5>Requirements</h5>
		<em>Requirement 4: Add a Domain Name to your reference implementation by:</em>
		<ol type='n'>
			<li>Purchasing a low cost Domain Name from GoDaddy</li>
			<li>Configuring the Domain Name to point to your cloud provider hosted reference implementation</li>
			<li>Create a second tutorial that demonstrates how to add a domain name to a website in your cloud provider</li>
		</ol>

		<em>Requirement 5: Add 3rd party authentication to your reference implementation by:</em>
		<ol type='n'>
			<li>Reviewing Google {firebaseTutorial()}</li>
			<li>Implementing 3rd party authentication (i.e. Google) and login to your reference implementation</li>
			<li>Create a third tutorial that demonstrates how to add 3rd party authentication</li>
		</ol>

		<em>Requirement 6 (optional this sprint): Add server-side NoSQL database access by:</em>
		<ol type='n'>
			<li>This requirement is optional this sprint</li>
			<li>Implementing server-side NoSQL database access that stores user data from each login</li>
			<li>Implementing client functionality that displays login information from everyone that has accessed the NoSQL database</li>
			<li>Create a fourth tutorial that demonstrates how to stores and display login information</li>
		</ol>
	</div>)
}

export const architectureDesignLewisPhase2 = { 'name':'Lewis Instructional Software Architecture - Phase 2', 'route':'architecture-lewis-phase-2', 'render':renderArchitectureDesignLewisPhase2 }
export const architectureDesignLewisPhase2Link = () => { return makeNavigationLink(architectureDesignLewisPhase2) }

const renderArchitectureDesignLewisPhase3 = () => {
	return ( <div className="architecture-design-lewis">
		<h3>{architectureDesignLewisPhase3.name}</h3>
		<h5>Summary</h5>
		<p>In this activity you will continue to work as part of a team to review and enhance what you delivered 
		in {architectureDesignLewisPhase2Link()}. The objective continues to be the same, but in this sprint you 
		will add that several additional key application architecture elements to your reference implementation. 
		First, you finish adding a NoSQL database access. Then you will add file upload and download capabilities.
		Finally, you will create a “Hello World” style tutorial that configures and demonstrates these new 
		capabilities.</p>

		<h5>Objectives</h5>
		Students will: 
		<ul>
			<li><em>Host a fully functional instance of their reference implementation on a selected cloud provider</em></li>
			<li>Work as part of a team to consolidate, document, and simplify their Lewis Instructional Software Architecture.</li>
			<li><em>Succinctly</em> document the resulting architecture</li>
			<li>Provide a initial client only CI/CD reference implementation with GitHub and Azure, Firebase, AWS, or Docker.</li>
			<li>Add NoSQL database access and file upload/download capabilities to the reference implementation.</li>
		</ul>

		<h5>Scenario</h5>
		<p>You and your team did a wonderful job implementing {architectureDesignLewisLink()} and {architectureDesignLewisPhase1Link()}. 
		Now your team has been assigned as to enhance the architecture by adding secure NoSQL data storage and file upload 
		capabilities to the Lewis Instructional Architecture on Azure, Firebase, or AWS. </p>

		<p>The reference implementation must continue to:</p>
		<ul>
			<li>Demonstrate a React web application deployed as a static website running on the selected cloud provider.</li>
			<li>Store all source code stored in shared GitHub repository.</li>
			<li>Provide full CI/CD integration such that team members can test locally, update code in GitHub, and code is automatically deployed</li>
			<li>Include a tutorial and updated documentation demonstrating the above deliverables that can be completed 15 minutes</li>
		</ul>

		<p>Now it must be enhanced to include secure NoSQL data storage and file upload capabilities to:</p>
		<ul>
			<li>Demonstrate your React web application calling server-side SOA service.</li>
			<li>Demonstrate uploading a image (png) and small video (mp4) file to your cloud provider.</li>
			<li>Provide full CI/CD integration such that team members can test locally, update code in GitHub, and code is automatically deployed</li>
			<li>Include a tutorial and updated documentation demonstrating the above deliverables that can be completed 15 minutes</li>
		</ul>

		<p>Note that requirements 1 through 3 from {architectureDesignLewisPhase1Link()} are not repeated. Requirement 
		4 is included again this sprint as it was not required to complete requirement 4 last sprint. 
		</p>

		<h5>Requirements</h5>
		<em>Requirement 6: Add server-side NoSQL database access by:</em>
		<ol type='n'>
			<li>Implementing server-side NoSQL database access that stores user data from each login</li>
			<li>Implementing client functionality that displays login information from everyone that has accessed the NoSQL database</li>
			<li>Create a fourth tutorial that demonstrates how to stores and display login information</li>
		</ol>

		<em>Requirement 7: Add server-side file storage and sharing by:</em>
		<ol type='n'>
			<li>Implementing file upload functionality</li>
			<li>Implementing display all files that have been uploaded and download file functionality</li>
			<li>Create a fifth tutorial that demonstrates how to upload, display, and download files</li>
		</ol>

		<em>Requirement 8: Create a tutorial that demonstrates creating a “Hello World” style application that</em>
		<ol type='n'>
			<li>Hosts a React application using GitHub for Continuous Integration</li>
			<li>Demonstrates Authentication with Google user credentials</li>
			<li>Connects a Domain name from GoDaddy</li>
			<li>Stores and retrieves data in a NoSQL database</li>
			<li>Uploads and downloads files</li>
		</ol>

	</div>)
}

export const architectureDesignLewisPhase3 = { 'name':'Lewis Instructional Software Architecture - Phase 3', 'route':'architecture-lewis-phase-3', 'render':renderArchitectureDesignLewisPhase3 }
export const architectureDesignLewisPhase3Link = () => { return makeNavigationLink(architectureDesignLewisPhase3) }